import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authorization';

const API = process.env.REACT_APP_API_NODE_URL;
const CODE_LENGTH = 6;
const EMAIL_REGEX = /\S+@\S+\.\S+/;

export default function SignInWithCode() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const getErrorMessage = (e: unknown) =>
    e instanceof Error ? e.message : String(e);

  const showError = (msg: string) => {
    setIsSuccess(false);
    setMessage(msg);
  };

  const showSuccess = (msg: string) => {
    setIsSuccess(true);
    setMessage(msg);
  };

  const validateEmail = (): boolean => {
    if (!email.trim()) {
      showError('Please enter an email address.');
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      showError('Please enter a valid email address.');
      return false;
    }
    return true;
  };

  const handleRequestCode = async () => {
    if (!validateEmail()) return;

    setMessage('');
    setLoading(true);
    setShowRetry(false);

    try {
      const res = await fetch(`${API}auth/code/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const payload = await res.json().catch(() => null);

      if (res.status === 201) {
        setCodeSent(true);
        showSuccess('A 6-digit code has been sent to your email.');
      } else if (res.status === 200) {
        setCodeSent(true);
        showSuccess('A code was already sent to your email. Please check your inbox.');
      } else {
        showError(payload?.error || payload?.message || `HTTP ${res.status}`);
      }
    } catch (e) {
      if ((e as any)?.name === 'AbortError') {
        showError('Request was interrupted. Please try again.');
        setShowRetry(true);
        return;
      }
      showError(`An error occurred while sending the code: ${getErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!code.trim()) {
      showError('Please enter the code.');
      return;
    }

    setMessage('');
    setLoading(true);
    setShowRetry(false);

    try {
      const res = await fetch(`${API}auth/code/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code: code.trim() }),
      });

      const payload = await res.json().catch(() => null);

      if (res.ok) {
        await refresh();
        navigate('/stores', { replace: true });
      } else {
        showError(payload?.error || payload?.message || `HTTP ${res.status}`);
      }
    } catch (e) {
      if ((e as any)?.name === 'AbortError') {
        showError('Request was interrupted. Please try again.');
        setShowRetry(true);
        return;
      }
      showError(`An error occurred: ${getErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmailStep = () => {
    setCodeSent(false);
    setCode('');
    setMessage('');
    setIsSuccess(false);
    setShowRetry(false);
  };

  if (!codeSent) {
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); handleRequestCode(); }}
        className="login-form"
      >
        <input
          type="email"
          name="email"
          className="login-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete='email'
          required
          placeholder="Email address"
        />
        {showRetry ? (
          <button type="button" className='login-button' onClick={handleRequestCode}>
            Retry
          </button>
        ) : (
          <button className='login-button' disabled={loading}>
            {loading ? 'Sending...' : 'Send sign-in code'}
          </button>
        )}
        {message !== '' && (
          <span className={isSuccess ? 'success' : 'error-message'}>{message}</span>
        )}
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); handleRedeemCode(); }}
      className="login-form"
    >
      <p className="login-form__hint">
        Enter the {CODE_LENGTH}-digit code sent to <strong>{email}</strong>
      </p>
      <input
        type="text"
        name="code"
        className="login-input login-input--code"
        value={code}
        onChange={(e) => {
          const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
          setCode(digitsOnly);
        }}
        autoComplete='one-time-code'
        inputMode='numeric'
        maxLength={CODE_LENGTH}
        required
        placeholder={'0'.repeat(CODE_LENGTH)}
        autoFocus
      />
      {showRetry ? (
        <button type="button" className='login-button' onClick={handleRedeemCode}>
          Retry
        </button>
      ) : (
        <button className='login-button' disabled={loading || code.length < CODE_LENGTH}>
          {loading ? 'Verifying...' : 'Sign in'}
        </button>
      )}
      <button type="button" className='login-button login-button--secondary' onClick={handleBackToEmailStep}>
        Back
      </button>
      {message !== '' && (
        <span className={isSuccess ? 'success' : 'error-message'}>{message}</span>
      )}
    </form>
  );
}